/** @format */
import angular from 'angular';
import { firstValueFrom, lastValueFrom, of, throwError, EmptyError } from 'rxjs';
import { AuthTokenInterceptorService } from '../http-interceptors/auth-token-interceptor.service';
import { AuthService as NgAuthService } from './auth-service';
import { TokenStorageService } from './token-storage.service';

export const authServiceModule = 'sis-common.auth.service';

/**
 * This is a simplified version of the HttpRequest interface from Angular's HttpClient.
 * It is used to make the request object of the request and responseError methods compatible
 * with an implementation {@link AuthTokenInterceptorService} that is shared by the Angular and AngularJS http-clients
 */
export const httpRequestLike = request => ({
    ...request,
    body: request?.data,
    headers: {
        ...request?.headers,
        get: key => request?.headers[key],
        delete: key => delete request?.headers[key],
    },
    clone: updated => {
        const newHeaders = {
            ...request?.headers,
            ...updated?.setHeaders,
        };
        return {
            ...request,
            headers: {
                ...newHeaders,
                get: key => newHeaders[key],
                delete: key => delete newHeaders[key],
            },
        };
    },
});

// As opposed to AngularJs interceptors, Angular HttpInterceptor doesn't have a separate method for handling errors.
// Throw an error to handle it as such.
export const asError = {
    handle: response => throwError(response),
};
export const next = {
    handle: request => of(request),
};

(function () {
    angular
        .module(authServiceModule, [
            NgAuthService.downgrade.moduleName,
            AuthTokenInterceptorService.downgrade.moduleName,
            TokenStorageService.downgrade.moduleName,
        ])
        .factory('TokenStorage', TokenStorage)
        .factory('TokenAuthInterceptor', TokenAuthInterceptor)
        .factory('BootstrappingDeferralInterceptor', BootstrappingDeferralInterceptor)
        .factory('AuthService', AuthService)
        .config(configAuth);

    /**
     * @ngInject
     */
    function configAuth($httpProvider) {
        $httpProvider.interceptors.push('BootstrappingDeferralInterceptor');
        $httpProvider.interceptors.push('TokenAuthInterceptor');
    }

    /**
     * @ngInject
     */
    function TokenStorage($window, $log, $rootScope, $injector) {
        return {
            store: function (token, tokenExpiresIn, sessionTimeoutAt) {
                return $injector.get('tokenStorageService').store(token, tokenExpiresIn, sessionTimeoutAt);
            },
            retrieve: function () {
                return $injector.get('tokenStorageService').retrieve();
            },
            clear: function () {
                $injector.get('tokenStorageService').clear();
            },
            logout: function () {
                $injector.get('tokenStorageService').logout();
            },
        };
    }

    /**
     * @ngInject
     */
    function TokenAuthInterceptor($q, $injector) {
        return {
            request: function (config) {
                const interception$ = $injector
                    .get(AuthTokenInterceptorService.downgrade.serviceName)
                    .handleRequest(httpRequestLike(config), next);
                return firstValueFrom(interception$).catch(error => handleError($q, error));
            },
            responseError: function (error) {
                const interception$ = $injector
                    .get(AuthTokenInterceptorService.downgrade.serviceName)
                    .handleResponse(httpRequestLike(error?.config), asError);
                return firstValueFrom(interception$).catch(() => handleError($q, error));
            },
        };
    }

    /* When an observable returns empty, firstValueFrom throws an EmptyError.
    We want to ignore those as they should not raise an error. This maybe could have been done in errorservice.js as well? */
    function handleError($q, error) {
        if (error instanceof EmptyError) {
            return $q.reject({ config: { bypassErrorInterceptor: true } });
        }
        return $q.reject(error);
    }

    /**
     * @ngInject
     * Defers any requests originating from AngularJs $http until the Angular app has been initialized
     * If the Angular app has been initialized, the request is passed through immediately
     * This is needed because of requests that are made from AngularJs during the bootstrap phase, such as JSDataModel initializations
     */
    function BootstrappingDeferralInterceptor($q, $timeout, $rootScope) {
        return {
            request: function (config) {
                if ($rootScope.NgAppInitialized) {
                    return config;
                }
                const deferred = $q.defer();
                $rootScope.$on('NgAppInitialized', () => deferred.resolve(config));
                return deferred.promise;
            },
        };
    }

    /**
     * @ngInject
     */
    function AuthService($injector, $rootScope, $q) {
        // NOSONAR
        const api = {
            setAnonymousUrls: function (includedUrls, excludedUrls) {
                $injector.get('ngAuthService').setAnonymousUrls(includedUrls, excludedUrls);
            },
            isAnonymousUrl: function (url) {
                $injector.get('ngAuthService').isAnonymousUrl(url);
            },
            logout: function () {
                $injector.get('ngAuthService').logout();
            },
            username: function () {
                return $injector.get('ngAuthService').parseUser()?.username;
            },
            personId: function () {
                return $injector.get('ngAuthService').parseUser()?.id;
            },
            roles: function () {
                return $injector.get('ngAuthService').parseUser()?.roles;
            },
            scope: function () {
                return $injector.get('ngAuthService').parseUser()?.scope;
            },
            hasScope: function (s) {
                return $injector.get('ngAuthService').hasScope(s);
            },
            displayname: function () {
                return $injector.get('ngAuthService').parseUser()?.displayname;
            },
            firstNameInitialLetter: function () {
                return $injector.get('ngAuthService').parseUser()?.firstNameInitialLetter;
            },
            lastNameInitialLetter: function () {
                return $injector.get('ngAuthService').parseUser()?.lastNameInitialLetter;
            },
            loggedIn: function () {
                return !!$injector.get('ngAuthService').parseUser();
            },
            initializeAppAuth: function () {
                const deferred = $q.defer();
                deferred.resolveAppAuthInitialization = resolveAppAuthInitialization;

                if ($rootScope.NgAppInitialized) {
                    deferred.resolveAppAuthInitialization();
                }

                $rootScope.$on('NgAppInitialized', () => {
                    deferred.resolveAppAuthInitialization();
                });
                return deferred.promise;
            },
            preAuth: function () {
                return lastValueFrom($injector.get('ngAuthService').refreshAuthToken());
            },
            moveToAuthUrl: function (shibbolethPath, loginTargetUrl) {
                $injector.get('ngAuthService').moveToAuthUrl(shibbolethPath, loginTargetUrl);
            },
        };

        function resolveAppAuthInitialization() {
            return $injector
                .get('ngAuthService')
                .initializeAppAuth()
                .subscribe(res => {
                    this.resolve(res);
                });
        }

        return api;
    }

    configAuth.$inject = ['$httpProvider'];
    AuthService.$inject = ['$injector', '$rootScope', '$q'];
    BootstrappingDeferralInterceptor.$inject = ['$q', '$timeout', '$rootScope'];
    TokenAuthInterceptor.$inject = ['$q', '$injector'];
    TokenStorage.$inject = ['$window', '$log', '$rootScope', '$injector'];
})();
